/**
 * Writing Town progress, unlock, and resume selectors.
 * Mirrors iOS WritingTownProgressStore + per-lesson WritingLessonNProgressStore.
 *
 * Town course: writing.town — prefix unlock on the 11 visible stop IDs.
 * Lesson courses: writing.lesson2 … writing.lesson12 — active game IDs only.
 * familySort is never required for completion.
 */

import {
  WRITING_ACTIVITY_IDS_BY_LESSON,
  WRITING_TOWN_ID_SET,
  WRITING_TOWN_IDS,
  WRITING_TOWN_KEY,
  WRITING_TOWN_LESSONS,
  courseKeyForWritingLesson,
  writingTownById,
  writingTownByLessonNumber,
} from "../data/writingTownCatalog";

export function completedTownIds(courses) {
  const raw = courses?.[WRITING_TOWN_KEY];
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const ids = [];
  for (const id of raw) {
    if (typeof id !== "string" || !WRITING_TOWN_ID_SET.has(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return WRITING_TOWN_IDS.filter((id) => seen.has(id));
}

export function completedTownSet(courses) {
  return new Set(completedTownIds(courses));
}

export function completedWritingActivityIds(courses, lessonNumber) {
  const allowed = WRITING_ACTIVITY_IDS_BY_LESSON[Number(lessonNumber)];
  if (!allowed) return [];
  const raw = courses?.[courseKeyForWritingLesson(lessonNumber)];
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const ids = [];
  for (const id of raw) {
    if (typeof id !== "string" || !allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function completedWritingActivitySet(courses, lessonNumber) {
  return new Set(completedWritingActivityIds(courses, lessonNumber));
}

export function isWritingTownUnlocked(townId, townCompleted) {
  const lesson = writingTownById(townId);
  if (!lesson) return false;
  const index = WRITING_TOWN_LESSONS.findIndex((l) => l.townId === townId);
  if (index <= 0) return true;
  const completed = townCompleted instanceof Set ? townCompleted : new Set(townCompleted || []);
  return completed.has(WRITING_TOWN_LESSONS[index - 1].townId);
}

export function isWritingTownComplete(townId, townCompleted) {
  const completed = townCompleted instanceof Set ? townCompleted : new Set(townCompleted || []);
  return completed.has(townId);
}

export function nextWritingTownLesson(townCompleted) {
  const completed = townCompleted instanceof Set ? townCompleted : new Set(townCompleted || []);
  return WRITING_TOWN_LESSONS.find((lesson) => !completed.has(lesson.townId)) || null;
}

export function isWritingLessonComplete(lesson, courses) {
  const resolved = typeof lesson === "number" ? writingTownByLessonNumber(lesson) : lesson;
  if (!resolved) return false;
  const done = completedWritingActivitySet(courses, resolved.lessonNumber);
  return resolved.activities.every((activity) => done.has(activity.id));
}

export function firstUnfinishedWritingActivity(lesson, courses) {
  const resolved = typeof lesson === "number" ? writingTownByLessonNumber(lesson) : lesson;
  if (!resolved) return null;
  const done = completedWritingActivitySet(courses, resolved.lessonNumber);
  return resolved.activities.find((activity) => !done.has(activity.id)) || resolved.activities[resolved.activities.length - 1];
}

export function canOpenWritingTownLesson(townId, townCompleted) {
  return isWritingTownUnlocked(townId, townCompleted);
}

export function writingTownRoute() {
  return "/learn/writing";
}

export function writingLessonRoute(townId, activityId) {
  if (!activityId) return `/learn/writing/lesson/${townId}`;
  return `/learn/writing/lesson/${townId}/${activityId}`;
}

/**
 * Resolve a Writing player deep-link. Locked or unknown routes redirect safely.
 */
export function resolveWritingPlayerRoute({ townLessonId, activityId, courses }) {
  const lesson = writingTownById(townLessonId);
  if (!lesson) {
    return { redirectTo: writingTownRoute() };
  }

  const townCompleted = completedTownSet(courses);
  if (!canOpenWritingTownLesson(lesson.townId, townCompleted)) {
    return { redirectTo: writingTownRoute() };
  }

  const resume = firstUnfinishedWritingActivity(lesson, courses);
  const known = lesson.activities.some((a) => a.id === activityId);
  if (!activityId || !known) {
    return {
      redirectTo: writingLessonRoute(lesson.townId, resume.id),
    };
  }

  return {
    lesson,
    activity: lesson.activities.find((a) => a.id === activityId),
    redirectTo: null,
  };
}

export function isWritingCourseComplete(townCompleted) {
  const completed = townCompleted instanceof Set ? townCompleted : new Set(townCompleted || []);
  return WRITING_TOWN_IDS.every((id) => completed.has(id));
}

export function writingTownNodeState(townId, townCompleted) {
  if (isWritingTownComplete(townId, townCompleted)) return "complete";
  if (isWritingTownUnlocked(townId, townCompleted)) return "current";
  return "locked";
}

export function selectLearnWritingProgress(courses) {
  const townIds = completedTownIds(courses);
  const townSet = new Set(townIds);
  const nextTownLesson = nextWritingTownLesson(townSet);
  const resumeActivity = nextTownLesson
    ? firstUnfinishedWritingActivity(nextTownLesson, courses)
    : null;

  return {
    writingTownIds: townIds,
    writingTownSet: townSet,
    nextWritingTownLesson: nextTownLesson,
    nextWritingLesson: nextTownLesson
      ? { number: nextTownLesson.townNumber, townId: nextTownLesson.townId }
      : null,
    resumeWritingActivity: resumeActivity,
    writingDone: townIds.length,
    writingFinished: isWritingCourseComplete(townSet),
  };
}

export function unionWritingIds(existing, incoming, allowed) {
  const seen = new Set();
  const out = [];
  for (const id of [...(existing || []), ...(incoming || [])]) {
    if (typeof id !== "string" || seen.has(id)) continue;
    if (allowed && !allowed.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}
